import { Location } from '../../../../types/find';
import { AddressWithDistanceAndPhone } from '../../../../types';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';

dayjs.extend(objectSupport);

const DAY_NAME_MAP = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const DAYS_ORDER = Object.values(DAY_NAME_MAP);

export const formatOpeningHours = (
  openingHours: Location['openingHours'] | undefined,
): AddressWithDistanceAndPhone['openingHours'] => {
  const groupDays = (): Array<{ daysRange: string[]; open: string; close: string }> => {
    if (!openingHours?.length) return [];

    const hoursMap: Record<string, { open: string; close: string }> = Object.fromEntries(
      openingHours.map(({ dayOfWeek, openTime, closeTime }) => {
        const abbreviatedDay = DAY_NAME_MAP[dayOfWeek];
        return [abbreviatedDay, { open: openTime, close: closeTime }];
      }),
    );

    return DAYS_ORDER.reduce<Array<{ daysRange: string[]; open: string; close: string }>>(
      (results, day) => {
        if (!hoursMap[day]) return results;

        const { open, close } = hoursMap[day];
        const lastGroup = results[results.length - 1];

        if (lastGroup && lastGroup.open === open && lastGroup.close === close) {
          return [
            ...results.slice(0, -1),
            { ...lastGroup, daysRange: [...lastGroup.daysRange, day] },
          ];
        } else {
          return [...results, { daysRange: [day], open, close }];
        }
      },
      [],
    );
  };

  return groupDays().map((group) => {
    return {
      daysRange: formatDayRange(group.daysRange),
      open: format24HTimeTo12H(group.open),
      close: format24HTimeTo12H(group.close),
    };
  });
};

// Format day ranges (e.g., "Mon-Thu" instead of "Mon, Tue, Wed, Thu")
const formatDayRange = (days: string[]): string => {
  if (days.length === 1) {
    return days[0];
  }

  const sortedDays = [...days].sort((a, b) => DAYS_ORDER.indexOf(a) - DAYS_ORDER.indexOf(b));

  let isConsecutive = true;
  for (let i = 1; i < sortedDays.length; i++) {
    const currentDayIndex = DAYS_ORDER.indexOf(sortedDays[i]);
    const prevDayIndex = DAYS_ORDER.indexOf(sortedDays[i - 1]);

    if (currentDayIndex !== prevDayIndex + 1) {
      isConsecutive = false;
      break;
    }
  }

  return isConsecutive
    ? `${sortedDays[0]}-${sortedDays[sortedDays.length - 1]}`
    : sortedDays.join(', ');
};

const format24HTimeTo12H = (time24HoursFormat: string): string => {
  const [hours, minutes] = time24HoursFormat.split(':');
  const time = dayjs({ hour: hours, minute: minutes ?? 0 });

  return time.minute() === 0 ? time.format('ha') : time.format('h:mma');
};
